import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { GameContext } from "../../../components/Providers/GameContext";
import { useTheme } from "../../../components/Providers/ThemeContext";
import { ZodiacFEN } from "./ZodiacFEN";
import styles from "./FENHistory.module.css";
import { Mode, MoveType, Position, State } from "../types";
import ZodiacEngineService from "../../../components/ZodiacEngineService";
import {
  getAlgebraicNotation,
  getPieceDisplayName,
  generateMoveDescription,
} from "./moveDescriptionUtils";
import { parseFEN, sketchBoard, inferMoveFromFENDelta } from "./fenDeltaUtils";
import TimeControlManager from "../../timeControl/TimeControlManager";

interface MoveEntry {
  fen: string;
  turnNumber: number | string;
  isLightMove: boolean;
  simplified: string;
  moveType?: MoveType;
  fromPosition?: Position;
  toPosition?: Position;
  pieceType?: string;
  isPreviousGameResult?: boolean;
  isGameEndNotification?: boolean;
  victoryHeadline?: string;
  victoryDetails?: string;
  moveDescription: string;
}

// Interface for storing piece information including type
interface PieceInfo {
  id: string;
  type: string;
  isLight: boolean;
}

const FENHistory = forwardRef(
  ({ moveHistoryResetKey }: { moveHistoryResetKey?: number }, ref) => {
    const { gameState, updateGameState } = useContext(GameContext)!;
    const { theme } = useTheme();
    const [moveHistory, setMoveHistory] = useState<MoveEntry[]>([]);
    const [lastTurnCount, setLastTurnCount] = useState<number>(0);
    const [lastMode, setLastMode] = useState<Mode | null>(null);
    const [lastGameId, setLastGameId] = useState<string | null>(null);
    const [selectedMove, setSelectedMove] = useState<number | null>(null);
    const [lastKnownPositions, setLastKnownPositions] = useState<
      Map<string, Position>
    >(new Map());
    const [previousGameLastMove, setPreviousGameLastMove] = useState<
      string | null
    >(null);
    const [previousGameWinner, setPreviousGameWinner] = useState<string | null>(
      null
    );
    const [lastKnownPieceInfo, setLastKnownPieceInfo] = useState<
      Map<string, PieceInfo>
    >(new Map());
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [showPopup, setShowPopup] = useState<number | null>(null);
    const scrollBoxRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    // Check for dark mode from theme context or DOM
    useEffect(() => {
      if (theme) {
        // Use theme context if available (ChallengeAI page)
        setIsDarkMode(theme === "dark");
      } else {
        // Fall back to DOM observation (BoardContainer page)
        const checkDarkMode = () => {
          const darkElement = document.querySelector(".dark");
          setIsDarkMode(darkElement !== null);
        };

        checkDarkMode();

        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ["class"],
          subtree: true,
        });

        return () => observer.disconnect();
      }
    }, [theme]);

    // Close popup when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          popupRef.current &&
          !popupRef.current.contains(event.target as Node)
        ) {
          setShowPopup(null);
        }
      };

      if (showPopup !== null) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showPopup]);

    // Function to mask king information in FEN strings
    const maskKingsInFEN = (fen: string): string => {
      // If not in AI mode or NN test mode, or if in TestAI mode, return the original FEN
      if (gameState.mode !== Mode.AI) {
        return fen;
      }
      // Remove king markers from the FEN
      let processedFen = fen;
      let turnIndicator = "";
      if (fen.startsWith("d")) {
        turnIndicator = "d";
        processedFen = fen.substring(1);
      }
      const maskedFen = processedFen
        .replace(/([A-Za-z])K/g, "$1")
        .replace(/([A-Za-z])k/g, "$1");
      return turnIndicator + maskedFen;
    };

    // Utility to strip the bonus turn suffix from a FEN string
    function stripBonusSuffix(fen: string): string {
      return fen.replace(/\~:[a-f0-9\-]+/i, "");
    }

    // Utility to normalize ASCII sparkle (*) to unicode (✳️) for display
    // This is the single source of truth for FEN display normalization
    function normalizeSparkle(fen: string): string {
      return fen.replace(/\*/g, '✳️');
    }

    // Get displayable FEN (masked if in AI mode, but NOT in TestAI mode)
    const getDisplayFEN = (fen: string): string => {
      let cleanFen = stripBonusSuffix(fen);
      // Strip placement/moved annotations
      cleanFen = cleanFen.replace(/~\{(?:placement|moved):[^}]+\}/, "");
      if (gameState.mode === Mode.AI) {
        return maskKingsInFEN(cleanFen);
      }
      // In TestAI mode or any other mode, show kings
      return cleanFen;
    };

    // Helper to infer which player just moved based on FEN turn indicator
    function inferWasLightMove(prevFEN: string, currFEN: string): boolean {
      const prevIsDark = prevFEN.startsWith("d");
      const currIsDark = currFEN.startsWith("d");
      // If it's now dark's turn, Light just moved; if it's now light's turn, Dark just moved
      return currIsDark;
    }

  // Record the previous game winner and last move when a game ends in NN test mode
  useEffect(() => {

    // CRITICAL FIX: Don't process game end if we're in PieceSelection (new game starting)
    // This prevents old game victory notifications from appearing in new game history
    if (gameState.state === State.PieceSelection) {
      return;
    }

    // CRITICAL FIX: Don't process game end if moveHistory was just reset (only initial position)
    // AND turnCount is high (indicates stale gameState from old game)
    if (moveHistory.length === 1 && gameState.turnCount > 5) {
      return;
    }

      if (
        gameState.state === State.PostGame ||
        (gameState.state as string) === "Draw"
      ) {
        // Prevent duplicate game end notifications
        const alreadyHasGameEnd = moveHistory.some(
          (entry) => entry.isGameEndNotification
        );
        if (alreadyHasGameEnd) {
          return;
        }

        // Save the winner (if there is one)
        if (gameState.lightWon !== undefined) {
          const winner = gameState.lightWon ? "Light" : "Dark";
          setPreviousGameWinner(winner);
        } else {
          setPreviousGameWinner("No winner");
        }

        // Save the last move description if there are any moves in the history
        if (moveHistory.length > 1) {
          // More than just the initial position
          const lastMove = moveHistory[moveHistory.length - 1];
          setPreviousGameLastMove(lastMove.moveDescription);
        }

        // Ensure the final board state is recorded in the FEN history
        // This addresses the issue where the last game state isn't shown
        const currentFen = ZodiacFEN.encodeWithoutKings(gameState);
        const lastHistoryFen =
          moveHistory.length > 0 ? moveHistory[moveHistory.length - 1].fen : "";

        // Calculate move details once for both blocks
        // Use lastMoveWasLight if available, otherwise infer from winner or fall back to !isLightTurn
        let wasLightMove: boolean;
        if (gameState.lastMoveWasLight !== undefined) {
          wasLightMove = gameState.lastMoveWasLight;
        } else if (gameState.lightWon !== undefined) {
          // Fallback: Winner made the last move in capture/race victories
          wasLightMove = gameState.lightWon;
        } else {
          // Final fallback: Use turn indicator
          wasLightMove = !gameState.isLightTurn;
        }
        const {
          pieceType,
          fromPosition,
          toPosition,
          moveType,
          capturedPieceType,
          isSnakeShed,
          capturedPieceHadShed,
          isTreeWithHousedPiece,
          housedPieceType,
        } = determineMoveDetails();
        const previousFen =
          moveHistory.length > 0 ? moveHistory[moveHistory.length - 1].fen : "";
        let pieceAlreadyHoused = false;
        if (housedPieceType && previousFen) {
          pieceAlreadyHoused = wasAlreadyHoused(
            previousFen,
            housedPieceType.charAt(0),
            fromPosition || { row: 0, col: 0 }
          );
        }
        const {
          isTreeMoving,
          isLightTree,
          housedPieceType: fenHousedPieceType,
        } = isTreeMovedWithHousedPiece(currentFen, previousFen);
        const treeMovedWithHousedPiece =
          isTreeMoving &&
          ((isLightTree && wasLightMove) || (!isLightTree && !wasLightMove));
        const simplified =
          fromPosition && toPosition
            ? generateMoveDescription({
                pieceType,
                isLight: wasLightMove,
                from: [fromPosition.col, fromPosition.row],
                to: [toPosition.col, toPosition.row],
                moveType: moveType ? moveType.toLowerCase() : undefined,
                captured: capturedPieceType
                  ? { type: capturedPieceType, isLight: !wasLightMove }
                  : undefined,
              })
            : pieceType
            ? `${pieceType} moved`
            : "Unknown move";
        const finalTurnCount = gameState.turnCount + 1;

      // Only add the final FEN/move if it is not already present and not a duplicate
      const alreadyHasFinalFen = moveHistory.some((entry) => entry.fen === currentFen && !entry.isGameEndNotification);
      if (!alreadyHasFinalFen) {
        // CRITICAL: Additional sanity check - don't add high turn number moves to fresh history
        // This prevents ghost moves from old games appearing in new game history
        if (moveHistory.length <= 2 && gameState.turnCount > 10) {
        } else {
          // Use the wasLightMove value we calculated above (with proper fallbacks)
          const inferredWasLightMove = wasLightMove;
          // Patch: Rerun the FEN delta inference for the final move using the last two FENs
          const prevFen = moveHistory.length > 0 ? moveHistory[moveHistory.length - 1].fen : "";
          const moveDescription = prevFen && currentFen
            ? inferMoveFromFENDelta(prevFen, currentFen, inferredWasLightMove)
            : "Initial position";

          // CRITICAL FIX: Check if previous move has same turn number
          // If so, increment to avoid duplicate turn numbers (happens when game ends mid-turn)
          const lastMoveInHistory = moveHistory.length > 0 ? moveHistory[moveHistory.length - 1] : null;
          const finalTurnNumber = (lastMoveInHistory && lastMoveInHistory.turnNumber === gameState.turnCount)
            ? gameState.turnCount + 1
            : gameState.turnCount;

          setMoveHistory(prev => [
            ...prev,
            {
              fen: currentFen,
              turnNumber: finalTurnNumber,
              isLightMove: inferredWasLightMove,
              simplified: currentFen,
              moveDescription,
            },
          ]);
        }
      }

        // Only add the game end notification if not already present
        if (!alreadyHasGameEnd) {
          if (gameState.lightWon !== undefined) {
            const winner = gameState.lightWon ? "Light" : "Dark";
            let victoryType = "";
            let victoryDetails = "";

            // Check if the game ended due to timeout by checking TimeControlManager
            const timeState = TimeControlManager.getState();
            const isTimeoutVictory = timeState.timeExpired === true;

            if (isTimeoutVictory) {
              // Game ended by timeout
              victoryType = "Timeout";
              if (winner === "Light") {
                victoryDetails = `You won by opponent time-out!`;
              } else {
                victoryDetails = `You lost by time-out!`;
              }
            } else {
              // Detect king capture by checking FEN delta
              const prevFen =
                moveHistory.length > 0
                  ? moveHistory[moveHistory.length - 1].fen
                  : "";
              let wasKingCaptured = false;
              let capturedKingType = "";

              // Detect king capture by checking if the enemy king piece type exists on the board
              // Note: FEN uses encodeWithoutKings so king markers are hidden - we check board state instead
              if (winner === "Light" && gameState.darkKing) {
                // Check if dark king piece still exists on the board
                const darkKingStillExists = gameState.board.some((row) =>
                  row.some((square) =>
                    square.Pieces.some(
                      (piece) =>
                        !piece.isLight &&
                        piece.type === gameState.darkKing &&
                        piece.isKing
                    )
                  )
                );
                wasKingCaptured = !darkKingStillExists;
                capturedKingType = gameState.darkKing;
              } else if (winner === "Dark" && gameState.lightKing) {
                // Check if light king piece still exists on the board
                const lightKingStillExists = gameState.board.some((row) =>
                  row.some((square) =>
                    square.Pieces.some(
                      (piece) =>
                        piece.isLight &&
                        piece.type === gameState.lightKing &&
                        piece.isKing
                    )
                  )
                );
                wasKingCaptured = !lightKingStillExists;
                capturedKingType = gameState.lightKing;
              }

              if (winner === "Light") {
                if (wasKingCaptured && capturedKingType) {
                  victoryType = "Kingslayer";
                  victoryDetails = `Light player captured Dark ${capturedKingType} King`;
                } else if ((gameState as any).winCondition === 'zombie-race') {
                  victoryType = "Zombie Race";
                  victoryDetails = `Light player reached enemy home rank with their Zombie${
                    gameState.zombieRacePieceType
                      ? " " + gameState.zombieRacePieceType
                      : ""
                  } King`;
                } else {
                  const hasLightKingWonRace = gameState.board[
                    gameState.board.length - 1
                  ].some((square) =>
                    square.Pieces.some(
                      (piece) =>
                        piece.isLight && piece.type === gameState.lightKing
                    )
                  );
                  if (hasLightKingWonRace) {
                    victoryType = "Race Victory";
                    victoryDetails = `Light player's ${gameState.lightKing} King reached opponent's home rank`;
                  } else {
                    victoryType = "Victory";
                    victoryDetails = `Light player won`;
                  }
                }
              } else {
                if (wasKingCaptured && capturedKingType) {
                  victoryType = "Kingslayer";
                  victoryDetails = `Dark player captured Light ${capturedKingType} King`;
                } else if ((gameState as any).winCondition === 'zombie-race') {
                  victoryType = "Zombie Race";
                  victoryDetails = `Dark player reached enemy home rank with their Zombie${
                    gameState.zombieRacePieceType
                      ? " " + gameState.zombieRacePieceType
                      : ""
                  } King`;
                } else {
                  const hasDarkKingWonRace = gameState.board[0].some((square) =>
                    square.Pieces.some(
                      (piece) =>
                        !piece.isLight && piece.type === gameState.darkKing
                    )
                  );
                  if (hasDarkKingWonRace) {
                    victoryType = "Race Victory";
                    victoryDetails = `Dark player's ${gameState.darkKing} King reached opponent's home rank`;
                  } else {
                    victoryType = "Victory";
                    victoryDetails = `Dark player won`;
                  }
                }
              }
            }
            const victoryHeadline = `${winner} player wins by ${victoryType}!`;
            setMoveHistory((prev) => {
              const newHistory = [
                ...prev,
                {
                  fen: currentFen,
                  turnNumber: -2, // Special value to indicate this is a game end notification
                  isLightMove: wasLightMove,
                  simplified: "", // We'll use the victory properties instead
                  isPreviousGameResult: false,
                  isGameEndNotification: true, // New flag to identify this special entry
                  victoryHeadline,
                  victoryDetails,
                  moveDescription:
                    victoryHeadline +
                    (victoryDetails ? `\n${victoryDetails}` : ""),
                },
              ];
              return newHistory;
            });
          } else if ((gameState.state as string) === "Draw") {
            const drawHeadline = "Game is a Draw";
            // Use drawReason from gameState if present
            const drawReason =
              (gameState as any).drawReason || "No legal moves remain";
            setMoveHistory((prev) => [
              ...prev,
              {
                fen: currentFen,
                turnNumber: -2, // Special value to indicate this is a game end notification
                isLightMove: wasLightMove,
                simplified: "",
                isPreviousGameResult: false,
                isGameEndNotification: true,
                victoryHeadline: drawHeadline,
                victoryDetails: drawReason,
                moveDescription:
                  drawHeadline + (drawReason ? `\n${drawReason}` : ""),
              },
            ]);
          }
        }
      }
    }, [gameState.state, gameState.lightWon]); // Only trigger when game state changes to PostGame/Draw

    // Reset history when the game ID changes or mode changes
    useEffect(() => {
      const currentGameId =
        ZodiacEngineService.zodiacEngine.getGameId() || null;

      // Check if mode changed, game ID changed, or a game was reset (turnCount = 0 with a different lastTurnCount)
      if (
        lastMode !== gameState.mode ||
        (currentGameId && lastGameId !== currentGameId) ||
        (gameState.turnCount === 0 && lastTurnCount > 0) ||
        (gameState.mode === Mode.AI && gameState.turnCount === 0)
      ) {
        // Add this condition to force reset for AI games
        const initialFen = normalizeSparkle(ZodiacFEN.encodeWithoutKings(gameState));

        // Build the new history
        const newHistory: MoveEntry[] = [];

        // Add initial position
        newHistory.push({
          fen: initialFen,
          turnNumber: 0,
          isLightMove: true,
          simplified:
            gameState.mode === Mode.AI
              ? "AI Game Initial Position"
              : "Initial position",
          moveDescription: "Initial position",
        });

        setMoveHistory(newHistory);
        setLastTurnCount(gameState.turnCount);
        setLastMode(gameState.mode);
        setLastGameId(currentGameId); // currentGameId is now guaranteed to be string or null
        setSelectedMove(null);

        // Reset known positions
        setLastKnownPositions(new Map());
        updatePiecePositions(gameState.board);
      }
    }, [
      gameState.mode,
      gameState,
      lastTurnCount,
      lastMode,
      lastGameId,
      gameState.turnCount,
      previousGameWinner,
      previousGameLastMove,
    ]);

    // Tracking all piece positions to detect moves
    const updatePiecePositions = (board: any[][]) => {
      const newPositions = new Map<string, Position>();
      const newPieceInfo = new Map<string, PieceInfo>();

      // Scan the board for all pieces
      for (let row = 0; row < board.length; row++) {
        for (let col = 0; col < board[row].length; col++) {
          const square = board[row][col];
          if (square && square.Pieces) {
            for (const piece of square.Pieces) {
              // Create a unique ID for this piece (type + light/dark + other properties)
              const pieceId = `${piece.type}_${
                piece.isLight ? "light" : "dark"
              }_${piece.isKing ? "king" : ""}`;
              newPositions.set(pieceId, { row, col });

              // Store piece info
              newPieceInfo.set(pieceId, {
                id: pieceId,
                type: piece.type,
                isLight: piece.isLight,
              });
            }
          }
        }
      }

      setLastKnownPositions(newPositions);
      setLastKnownPieceInfo(newPieceInfo);
    };

    // Try to determine the move that was made by comparing current and previous board states
    const determineMoveDetails = (): {
      pieceType: string;
      fromPosition?: Position;
      toPosition?: Position;
      moveType?: MoveType;
      capturedPieceType?: string;
      isSnakeShed?: boolean;
      capturedPieceHadShed?: boolean;
      isTreeWithHousedPiece?: boolean;
      housedPieceType?: string;
    } => {
      const newPositions = new Map<string, Position>();
      const newPieceInfo = new Map<string, PieceInfo>();
      let movedPieceType = "";
      let fromPos: Position | undefined;
      let toPos: Position | undefined;
      let moveType: MoveType | undefined;
      let capturedPieceType: string | undefined;
      let isSnakeShed = false;
      let snakeRespawned = false;
      let capturedPieceHadShed = false;
      let isTreeWithHousedPiece = false;
      let housedPieceType: string | undefined;

      // Map of positions in the previous state
      // key: "row,col", value: array of piece IDs
      const positionToPieces = new Map<string, string[]>();

      // Track pieces by their position for comparison
      const positionMap = new Map<string, string[]>();

      // First map all the previous positions
      for (const [pieceId, position] of lastKnownPositions.entries()) {
        const posKey = `${position.row},${position.col}`;
        if (!positionToPieces.has(posKey)) {
          positionToPieces.set(posKey, []);
        }
        positionToPieces.get(posKey)?.push(pieceId);

        // Build a map of positions to piece IDs for easier lookup
        if (!positionMap.has(posKey)) {
          positionMap.set(posKey, []);
        }
        positionMap.get(posKey)?.push(pieceId);
      }

      // Create a set of all previous piece IDs to check for missing pieces later
      const previousPieceIds = new Set(lastKnownPositions.keys());

      // Track if we have a Snake (Light or Dark) in a position that could be captured
      let lightSnakeInDanger: {
        id: string;
        position: Position;
        hasShed?: boolean;
      } | null = null;
      let darkSnakeInDanger: {
        id: string;
        position: Position;
        hasShed?: boolean;
      } | null = null;

      // Find Light Snake position and check if it has the Shed ability (marked with *)
      // We need to check the actual board for this since our piece tracking doesn't store the ability state
      for (let row = 0; row < gameState.board.length; row++) {
        for (let col = 0; col < gameState.board[row].length; col++) {
          const square = gameState.board[row][col];
          if (square && square.Pieces) {
            for (const piece of square.Pieces) {
              // Check previous board state for snakes
              if (piece.type === "Snake") {
                for (const [id, pos] of lastKnownPositions.entries()) {
                  if (pos.row === row && pos.col === col) {
                    const pieceInfo = lastKnownPieceInfo.get(id);
                    if (pieceInfo?.type === "Snake") {
                      // Check if this snake has the shed ability
                      const hasShed = piece.hasShed || false;

                      if (pieceInfo.isLight) {
                        lightSnakeInDanger = { id, position: pos, hasShed };
                      } else {
                        darkSnakeInDanger = { id, position: pos, hasShed };
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      // New position map to compare with previous
      const newPositionMap = new Map<string, string[]>();

      // Scan the current board for all pieces and build new position map
      for (let row = 0; row < gameState.board.length; row++) {
        for (let col = 0; col < gameState.board[row].length; col++) {
          const square = gameState.board[row][col];
          if (square && square.Pieces) {
            const posKey = `${row},${col}`;
            if (!newPositionMap.has(posKey)) {
              newPositionMap.set(posKey, []);
            }

            // Check for Tree with housed pieces
            let isTree = false;
            let hasHousedPiece = false;
            let housedPieceId = "";

            for (const piece of square.Pieces) {
              if (piece.type === "Tree") {
                isTree = true;
              }

              // Create a unique ID for this piece
              const pieceId = `${piece.type}_${
                piece.isLight ? "light" : "dark"
              }_${piece.isKing ? "king" : ""}`;

              newPositionMap.get(posKey)?.push(pieceId);
              newPositions.set(pieceId, { row, col });

              // Store piece info
              newPieceInfo.set(pieceId, {
                id: pieceId,
                type: piece.type,
                isLight: piece.isLight,
              });

              // If there's a piece on the square that's not a Tree, it might be housed
              if (piece.type !== "Tree") {
                hasHousedPiece = true;
                housedPieceId = pieceId;
                housedPieceType = piece.type;
              }

              // Remove from previous set since we found this piece
              previousPieceIds.delete(pieceId);

              // Check if this piece has moved from its last known position
              if (lastKnownPositions.has(pieceId)) {
                const oldPos = lastKnownPositions.get(pieceId)!;

                // If the position is different, this is the piece that moved
                if (oldPos.row !== row || oldPos.col !== col) {
                  // If this is a Tree that's moved and the square also has a housed piece
                  if (piece.type === "Tree" && hasHousedPiece) {
                    movedPieceType = piece.type;
                    isTreeWithHousedPiece = true;
                  } else if (!isTreeWithHousedPiece) {
                    // Only set if we haven't found a Tree with housed piece
                    movedPieceType = piece.type;
                  }

                  fromPos = oldPos;
                  toPos = { row, col };

                  // Check for capturing a Snake
                  // 1. If a piece moved to a position where a snake was
                  // 2. And that snake is no longer there
                  // 3. This is a capture even if the snake respawned elsewhere
                  const oldPosKey = `${oldPos.row},${oldPos.col}`;
                  const newPosKey = `${row},${col}`;

                  const piecesAtNewPosOld = positionMap.get(newPosKey) || [];

                  // Check if there was a Snake at this new position previously
                  for (const oldPieceId of piecesAtNewPosOld) {
                    const oldPieceInfo = lastKnownPieceInfo.get(oldPieceId);

                    if (oldPieceInfo?.type === "Snake") {
                      // A Snake was here before our piece moved here
                      // Check if the Snake is now missing or has moved
                      let snakeFound = false;

                      for (const [
                        newPieceId,
                        newPos,
                      ] of newPositions.entries()) {
                        if (
                          newPieceId === oldPieceId &&
                          (newPos.row !== row || newPos.col !== col)
                        ) {
                          // Found the Snake elsewhere on the board
                          snakeFound = true;
                          break;
                        }
                      }

                      if (!snakeFound && !newPositions.has(oldPieceId)) {
                        // The Snake is gone from the board or has respawned with a new ID
                        moveType = MoveType.capture;
                        capturedPieceType = "Snake";

                        // Check if this Snake had the Shed ability
                        if (
                          oldPieceInfo.isLight &&
                          lightSnakeInDanger?.hasShed
                        ) {
                          isSnakeShed = true;
                          capturedPieceHadShed = true;
                        } else if (
                          !oldPieceInfo.isLight &&
                          darkSnakeInDanger?.hasShed
                        ) {
                          isSnakeShed = true;
                          capturedPieceHadShed = true;
                        }
                      } else {
                        // The Snake is still on the board, but in a different position
                        // This is also a capture if our piece is now where the Snake was
                        moveType = MoveType.capture;
                        capturedPieceType = "Snake";

                        // Check if this Snake had the Shed ability
                        if (
                          oldPieceInfo.isLight &&
                          lightSnakeInDanger?.hasShed
                        ) {
                          isSnakeShed = true;
                          capturedPieceHadShed = true;
                        } else if (
                          !oldPieceInfo.isLight &&
                          darkSnakeInDanger?.hasShed
                        ) {
                          isSnakeShed = true;
                          capturedPieceHadShed = true;
                        }
                      }
                    }
                  }

                  // Check if the new position had any other pieces in it before
                  if (piecesAtNewPosOld.length > 0 && !moveType) {
                    // If any of those pieces are now missing, it's likely a capture
                    const capturedPieces = piecesAtNewPosOld.filter(
                      (id) =>
                        // Piece is missing from board or moved elsewhere
                        !newPositions.has(id) ||
                        (newPositions.has(id) &&
                          (newPositions.get(id)!.row !== row ||
                            newPositions.get(id)!.col !== col))
                    );

                    if (capturedPieces.length > 0) {
                      moveType = MoveType.capture;

                      // Get the type of the captured piece from our stored info
                      const capturedPieceId = capturedPieces[0]; // Just take the first one if multiple
                      const capturedPiece =
                        lastKnownPieceInfo.get(capturedPieceId);
                      if (capturedPiece) {
                        capturedPieceType = capturedPiece.type;

                        // If we captured a snake, check if it has the shed ability
                        if (capturedPiece.type === "Snake") {
                          if (
                            capturedPiece.isLight &&
                            lightSnakeInDanger?.hasShed
                          ) {
                            isSnakeShed = true;
                            capturedPieceHadShed = true;
                          } else if (
                            !capturedPiece.isLight &&
                            darkSnakeInDanger?.hasShed
                          ) {
                            isSnakeShed = true;
                            capturedPieceHadShed = true;
                          }
                        }
                      }
                    }
                  }

                  // Special case: Detecting if a piece moved to a position where a Snake was
                  // If a Light Snake was at a position and is now missing, and this is where a piece moved to
                  if (
                    lightSnakeInDanger &&
                    lightSnakeInDanger.position.row === row &&
                    lightSnakeInDanger.position.col === col
                  ) {
                    const lightSnakeId = lightSnakeInDanger.id;

                    // Check if the Snake is gone or moved elsewhere
                    if (
                      !newPositions.has(lightSnakeId) ||
                      (newPositions.has(lightSnakeId) &&
                        (newPositions.get(lightSnakeId)!.row !== row ||
                          newPositions.get(lightSnakeId)!.col !== col))
                    ) {
                      // Light Snake was captured or moved
                      moveType = MoveType.capture;
                      capturedPieceType = "Snake";

                      // Check if this Snake had the Shed ability
                      if (lightSnakeInDanger.hasShed) {
                        isSnakeShed = true;
                        capturedPieceHadShed = true;
                      }
                    }
                  }

                  // If a Dark Snake was at a position and is now missing, and this is where a piece moved to
                  if (
                    darkSnakeInDanger &&
                    darkSnakeInDanger.position.row === row &&
                    darkSnakeInDanger.position.col === col
                  ) {
                    const darkSnakeId = darkSnakeInDanger.id;

                    // Check if the Snake is gone or moved elsewhere
                    if (
                      !newPositions.has(darkSnakeId) ||
                      (newPositions.has(darkSnakeId) &&
                        (newPositions.get(darkSnakeId)!.row !== row ||
                          newPositions.get(darkSnakeId)!.col !== col))
                    ) {
                      // Dark Snake was captured or moved
                      moveType = MoveType.capture;
                      capturedPieceType = "Snake";

                      // Check if this Snake had the Shed ability
                      if (darkSnakeInDanger.hasShed) {
                        isSnakeShed = true;
                        capturedPieceHadShed = true;
                      }
                    }
                  }

                  // If we haven't determined a move type yet, check piece state
                  if (!moveType) {
                    if (piece.isEntangled) {
                      //moveType = MoveType.simpleentangle;
                    } else if (piece.isInfected) {
                      moveType = MoveType.infect;
                    } else if (piece.isFainted) {
                      moveType = MoveType.faint;
                    } else {
                      // Default to regular move
                      moveType = MoveType.regular;
                    }
                  }
                }
              }
            }
          }
        }
      }

      // Check for Snake shed special case
      // If a Snake appears on the first rank after an opponent's turn, it was likely captured and used shed
      const wasLightMove = !gameState.isLightTurn;
      for (const [pieceId, position] of newPositions.entries()) {
        const pieceInfo = newPieceInfo.get(pieceId);

        if (pieceInfo?.type === "Snake") {
          // Is this a Snake on the first rank?
          const isOnFirstRank =
            (pieceInfo.isLight && position.row === 0) || // Light Snake on row 0
            (!pieceInfo.isLight && position.row === gameState.board.length - 1); // Dark Snake on last rank

          // Did the opponent just move?
          const isOpponentMove = pieceInfo.isLight !== wasLightMove;

          // Was this Snake not in our previous positions map?
          const isNewlyAppeared = !lastKnownPositions.has(pieceId);

          if (isOnFirstRank && isOpponentMove && isNewlyAppeared) {
            isSnakeShed = true;
            moveType = MoveType.capture; // A capture occurred
            capturedPieceType = "Snake";
            capturedPieceHadShed = true;
          }
        }
      }

      // If we detected that a piece moved to a square where a Snake was, and a Snake respawned,
      // it was definitely a capture + shed
      if (snakeRespawned && capturedPieceType === "Snake") {
        isSnakeShed = true;
        capturedPieceHadShed = true;
      }

      // Update known positions for next turn
      setLastKnownPositions(newPositions);
      setLastKnownPieceInfo(newPieceInfo);

      return {
        pieceType: movedPieceType,
        fromPosition: fromPos,
        toPosition: toPos,
        moveType,
        capturedPieceType,
        isSnakeShed,
        capturedPieceHadShed,
        isTreeWithHousedPiece,
        housedPieceType,
      };
    };

    // Check the FEN string to determine if a piece was already housed before the move
    const wasAlreadyHoused = (
      fen: string,
      pieceType: string,
      position: Position
    ): boolean => {
      // In FEN notation, a piece housed in a tree is indicated by T+ before the piece ID
      // First, remove any turn indicator
      const cleanFen = fen.startsWith("d") ? fen.substring(1) : fen;

      // Look for patterns like "T+B" or "T+U1" indicating a housed piece
      const housedPattern = new RegExp(`T\\+[${pieceType}]`);
      return housedPattern.test(cleanFen);
    };

    // Check if the FEN indicates a Tree that moved with a housed piece
    const isTreeMovedWithHousedPiece = (
      currentFen: string,
      previousFen: string
    ): {
      isTreeMoving: boolean;
      isLightTree: boolean;
      housedPieceType?: string;
    } => {
      if (!previousFen || !currentFen)
        return { isTreeMoving: false, isLightTree: true };

      // Remove turn indicators
      const cleanCurrentFen = currentFen.startsWith("d")
        ? currentFen.substring(1)
        : currentFen;
      const cleanPreviousFen = previousFen.startsWith("d")
        ? previousFen.substring(1)
        : previousFen;

      // Look for patterns like "T+S*K1" in the current FEN
      // The numeric suffix indicates the Tree moved with a housed piece

      // For Light Trees
      const lightTreeMovedPattern = /T\+([A-Za-z*]+)(\d+)/g;
      const lightMatches = Array.from(
        cleanCurrentFen.matchAll(lightTreeMovedPattern)
      );

      if (lightMatches && lightMatches.length > 0) {
        // Extract the piece character (excluding any special markers like *)
        const match = lightMatches[0];
        const pieceWithMarkers = match[1]; // This might include * for abilities
        const pieceChar = pieceWithMarkers.replace(/[*]/g, ""); // Remove * markers

        return {
          isTreeMoving: true,
          isLightTree: true,
          housedPieceType: getPieceDisplayName(pieceChar),
        };
      }

      // For Dark Trees
      const darkTreeMovedPattern = /t\+([A-Za-z*]+)(\d+)/g;
      const darkMatches = Array.from(
        cleanCurrentFen.matchAll(darkTreeMovedPattern)
      );

      if (darkMatches && darkMatches.length > 0) {
        // Extract the piece character (excluding any special markers like *)
        const match = darkMatches[0];
        const pieceWithMarkers = match[1]; // This might include * for abilities
        const pieceChar = pieceWithMarkers.replace(/[*]/g, ""); // Remove * markers

        return {
          isTreeMoving: true,
          isLightTree: false,
          housedPieceType: getPieceDisplayName(pieceChar),
        };
      }

      return { isTreeMoving: false, isLightTree: true };
    };

    // Determine the type of housed piece from FEN
    const getHousedPieceType = (fen: string): string | undefined => {
      if (!fen) return undefined;

      // Remove turn indicator
      const cleanFen = fen.startsWith("d") ? fen.substring(1) : fen;

      // Look for pattern "T+X" or "t+X" where X is the piece
      // Capital T for Light Tree, lowercase t for Dark Tree
      // The piece might have special markers like * so we need to handle that
      const lightHousedPattern = /T\+([A-Za-z*]+)/;
      const darkHousedPattern = /t\+([A-Za-z*]+)/;

      // First check for Light Tree with housed piece
      const lightMatch = cleanFen.match(lightHousedPattern);
      if (lightMatch && lightMatch[1]) {
        const pieceWithMarkers = lightMatch[1];
        const pieceChar = pieceWithMarkers.replace(/[*]/g, ""); // Remove * markers
        return getPieceDisplayName(pieceChar);
      }

      // Then check for Dark Tree with housed piece
      const darkMatch = cleanFen.match(darkHousedPattern);
      if (darkMatch && darkMatch[1]) {
        const pieceWithMarkers = darkMatch[1];
        const pieceChar = pieceWithMarkers.replace(/[*]/g, ""); // Remove * markers
        return getPieceDisplayName(pieceChar);
      }

      return undefined;
    };

    // Helper function to map piece character to full name
    const getPieceDisplayName = (pieceChar: string): string => {
      const pieceTypes: Record<string, string> = {
        B: "Bear",
        C: "Crane",
        F: "Frog",
        G: "Goat",
        M: "Monkey",
        U: "Fungi",
        O: "Octopus",
        P: "Penguin",
        R: "Rhino",
        S: "Snake",
        T: "Tree",
        W: "Wolf",
        b: "Bear",
        c: "Crane",
        f: "Frog",
        g: "Goat",
        m: "Monkey",
        u: "Fungi",
        o: "Octopus",
        p: "Penguin",
        r: "Rhino",
        s: "Snake",
        t: "Tree",
        w: "Wolf",
      };

      return pieceTypes[pieceChar] || "unknown";
    };

  // Add the current game state to history whenever the FEN history changes
  useEffect(() => {
    try {
      // CRITICAL FIX: Process ALL FENs from gameState.fenHistory
      // This includes bonus turn moves which don't increment turnCount
      if (gameState.state === State.Playing || gameState.state === State.PostGame) {
        // DEBUG: Log raw fenHistory before any processing
        const rawFenHistory = gameState.fenHistory || [];

        // Normalize * to ✳️ BEFORE filtering to handle Firebase encoding
        const normalizedHistory = rawFenHistory.map(fen => fen.replace(/\*/g, '✳️'));

        // Filter out ASCII duplicates - only keep unicode versions (✳️ instead of *)
        // After normalization, this filter should rarely remove anything
        const engineFenHistory = normalizedHistory.filter(fen => !fen.includes('*'));



          // CRITICAL: If fenHistory is shorter than moveHistory, the game was reset
          // BUT: NEVER reset if the game is in PostGame state (victory notification should persist)
          // AND: Don't reset if the extra entries are game end notifications or final position
          if (engineFenHistory.length < moveHistory.length) {
            // If the game has ended, DO NOT reset the history - the victory notification should remain
            if (gameState.state === State.PostGame) {
            } else {
              // Count game end notifications in move history
              const gameEndNotifications = moveHistory.filter(
                (e) => e.isGameEndNotification
              ).length;
              const movesWithoutNotifications =
                moveHistory.length - gameEndNotifications;

              // Allow a buffer of 1-2 extra entries for final position + victory notification
              // This accounts for the final move that might be added to UI before the next engine update
              const reasonableBuffer = 2;

              // Only reset if the engine has significantly fewer FENs than moves
              if (
                engineFenHistory.length <
                movesWithoutNotifications - reasonableBuffer
              ) {
                const initialFen = normalizeSparkle(
                  engineFenHistory[0] ||
                  ZodiacFEN.encodeWithoutKings(gameState)
                );
                setMoveHistory([
                  {
                    fen: initialFen,
                    turnNumber: 0,
                    isLightMove: true,
                    simplified: "Initial position",
                    moveDescription: "Initial position",
                  },
                ]);
                return;
              } else {
              }
            }
          }

          // Process any new FENs that aren't in our UI history yet
          for (let i = moveHistory.length; i < engineFenHistory.length; i++) {
            const currentFen = engineFenHistory[i];
            const prevFen = i > 0 ? engineFenHistory[i - 1] : "";

            // Check if this is a placement or move (has ~{placement:...} or ~{moved:...} annotation)
            const isPlacement = currentFen.includes("~{placement:") || currentFen.includes("~{moved:");

            // Extract turn count from @turnCount metadata if present
            // For placements without @turnCount, use "0" to indicate setup phase
            const turnCountMatch = currentFen.match(/@(\d+)$/);
            const turnNumber = turnCountMatch
              ? parseInt(turnCountMatch[1])
              : isPlacement
              ? 0
              : gameState.turnCount;

            // Remove metadata for comparison
            const cleanCurrentFen = currentFen
              .replace(/@\d+$/, "")
              .replace(/#\d+$/, "");
            const cleanPrevFen = prevFen
              .replace(/@\d+$/, "")
              .replace(/#\d+$/, "");

        // Prevent duplicate FENs (ignoring turn indicator and bonus suffix)
        const prevFenNoTurn = stripBonusSuffix(cleanPrevFen.replace(/^-?d?/, ""));
        const currFenNoTurn = stripBonusSuffix(cleanCurrentFen.replace(/^-?d?/, ""));

        // CRITICAL: Detect bonus turn end with no move (Virus end-turn)
        // If previous FEN had "-" prefix (bonus turn) and current doesn't,
        // but board state is identical, this is an end-turn with no move - skip it
        const prevWasBonusTurn = cleanPrevFen.startsWith("-");
        const currIsBonusTurn = cleanCurrentFen.startsWith("-");
        const isBonusTurnEnd = prevWasBonusTurn && !currIsBonusTurn && prevFenNoTurn === currFenNoTurn;

        // DETAILED DUPLICATE DETECTION LOGGING

        if (isBonusTurnEnd) {
        } else if (currFenNoTurn !== prevFenNoTurn || i === 0) {

            let moveDescription = "";
            if (prevFen && currentFen) {
              // SPECIAL DEBUG: Enhanced logging for the second entry (index 1)
              if (i === 1) {
              }
              // DEBUG: Log the FENs being compared for delta inference
              moveDescription = inferMoveFromFENDelta(cleanPrevFen, cleanCurrentFen);
            } else {
              moveDescription = "Initial position";
            }

            const isBonusTurn = cleanCurrentFen.startsWith("-");

            setMoveHistory((prev) => {
              // CRITICAL: Calculate fractional turn number for bonus turns
              // Bonus turns should display as X.1, X.2, etc.
              let displayTurnNumber: number | string = turnNumber;
              if (isBonusTurn) {
                // Count how many bonus turns with this turn number already exist in prev
                const bonusTurnCount = prev.filter((entry: MoveEntry) => {
                  const entryTurn = typeof entry.turnNumber === 'string'
                    ? parseFloat(entry.turnNumber)
                    : entry.turnNumber;
                  return Math.floor(entryTurn) === turnNumber && entry.turnNumber !== turnNumber;
                }).length;
                displayTurnNumber = `${turnNumber}.${bonusTurnCount + 1}`;
              }

              const newEntry: MoveEntry = {
                fen: cleanCurrentFen,
                turnNumber: displayTurnNumber,
                isLightMove: !gameState.isLightTurn,
                simplified: cleanCurrentFen,
                moveDescription,
              };

              const updated = [...prev, newEntry];

                // === DRAW NOTIFICATION (read from engine, don't recalculate) ===
                // The engine (FENHistoryManager) is the single source of truth for draw detection.
                // We only add a UI notification here if the engine has already determined a draw.
                const alreadyHasDraw = updated.some(
                  (e) =>
                    e.isGameEndNotification &&
                    e.victoryHeadline === "Game is a Draw"
                );
                const isRepetitionDraw =
                  gameState.state === State.Draw &&
                  (gameState as any).drawReason === "3 time repeat draw";

                if (isRepetitionDraw && !alreadyHasDraw) {
                  updated.push({
                    fen: newEntry.fen,
                    turnNumber: -2,
                    isLightMove: newEntry.isLightMove,
                    simplified: "",
                    isPreviousGameResult: false,
                    isGameEndNotification: true,
                    victoryHeadline: "Game is a Draw",
                    victoryDetails: "3 time repeat draw",
                    moveDescription: "Game is a Draw\n3 time repeat draw",
                  });
                }
                // === END DRAW NOTIFICATION ===

                return updated;
              });
            } else {
            }
          }
          setLastTurnCount(gameState.turnCount);
        }
      } catch (err) {
      }
    }, [gameState.fenHistory, gameState.state, moveHistory.length]);

    // Add initial state when the component mounts
    useEffect(() => {
      try {
        if (moveHistory.length === 0) {
          const initialFen = normalizeSparkle(ZodiacFEN.encodeWithoutKings(gameState));
          const currentGameId =
            ZodiacEngineService.zodiacEngine.getGameId() || null;
          setMoveHistory([
            {
              fen: initialFen,
              turnNumber: 0,
              isLightMove: true,
              simplified: "Initial position",
              moveDescription: "Initial position",
            },
          ]);
          setLastMode(gameState.mode);
          setLastGameId(currentGameId); // currentGameId is now guaranteed to be string or null

          // Initialize the piece positions tracking
          updatePiecePositions(gameState.board);
        }
      } catch (err) {
      }
    }, []);

    // Scroll to the bottom when new moves are added
    useEffect(() => {
      if (scrollBoxRef.current) {
        scrollBoxRef.current.scrollTop = scrollBoxRef.current.scrollHeight;
      }
    }, [moveHistory]);

    const handleMoveClick = (index: number) => {
      const move = moveHistory[index];
      if (move && !move.isGameEndNotification) {
        // Set this move as selected (highlight it)
        setSelectedMove(selectedMove === index ? null : index);
        // Show popup for selected move
        setShowPopup(selectedMove === index ? null : index);
      } else {
        // Toggle selection as before for game end notifications
        setSelectedMove(index === selectedMove ? null : index);
      }
    };

    const handleCopyFEN = async (index: number) => {
      const move = moveHistory[index];
      if (move) {
        try {
          const fenToCopy = getDisplayFEN(move.fen);
          await navigator.clipboard.writeText(fenToCopy);
          setShowPopup(null); // Close popup after copying
        } catch (err) {

          // Fallback for older browsers
          try {
            const textArea = document.createElement("textarea");
            textArea.value = getDisplayFEN(move.fen);
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
            setShowPopup(null);
          } catch (fallbackErr) {
          }
        }
      }
    };

    const handleRevertToFEN = (index: number) => {
      const move = moveHistory[index];
      if (move) {
        // TODO: Implement revert functionality
        setShowPopup(null); // Close popup after action
      }
    };

    // Expose resetMoveHistory to parent via ref
    useImperativeHandle(ref, () => ({
      resetMoveHistory: () => {
        const initialFen = normalizeSparkle(ZodiacFEN.encodeWithoutKings(gameState));
        setMoveHistory([
          {
            fen: initialFen,
            turnNumber: 0,
            isLightMove: true,
            simplified: "Initial position",
            moveDescription: "Initial position",
          },
        ]);
        setLastMode(gameState.mode);
        setLastGameId(ZodiacEngineService.zodiacEngine.getGameId() || null);
        setSelectedMove(null);
        setLastKnownPositions(new Map());
        updatePiecePositions(gameState.board);
      },
    }));

  // Reset move history when moveHistoryResetKey changes
  useEffect(() => {
    try {
      const initialFen = normalizeSparkle(ZodiacFEN.encodeWithoutKings(gameState));
      setMoveHistory([
        {
          fen: initialFen,
          turnNumber: 0,
          isLightMove: true,
          simplified: "Initial position",
          moveDescription: "Initial position",
        },
      ]);
      setLastMode(gameState.mode);
      setLastGameId(ZodiacEngineService.zodiacEngine.getGameId() || null);
      setLastTurnCount(gameState.turnCount); // CRITICAL: Update lastTurnCount to prevent race condition
      setSelectedMove(null);
      setLastKnownPositions(new Map());
      updatePiecePositions(gameState.board);
    } catch (err) {
    }
  }, [moveHistoryResetKey]);

  // State for copy confirmation popup
  const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);

    // Handler to copy full move history
    const handleCopyFullHistory = async () => {
      try {
        const historyText = moveHistory
          .map((move, index) => {
            if (move.isGameEndNotification) {
              return `! ${move.victoryHeadline}\n  ${move.victoryDetails}`;
            }
            return `${move.turnNumber}. ${
              move.moveDescription
            }\n   ${getDisplayFEN(move.fen)}`;
          })
          .join("\n\n");

        await navigator.clipboard.writeText(historyText);

      // Show confirmation popup
      setShowCopyConfirmation(true);

      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        setShowCopyConfirmation(false);
      }, 3000);
    } catch (err) {
      alert('Failed to copy move history to clipboard');
    }
  };

  // Click handler to dismiss copy confirmation
  useEffect(() => {
    if (showCopyConfirmation) {
      const handleClick = () => setShowCopyConfirmation(false);
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [showCopyConfirmation]);

  // Wrap the main render in a try/catch to catch any render errors
  try {
    return (
      <div className={`${styles.fenHistoryContainer} ${isDarkMode ? styles.dark : ''}`}>
        <div className={styles.headerRow}>
          <h3>Move History</h3>
          <button
            className={styles.copyButton}
            onClick={handleCopyFullHistory}
            title="Copy full move history to clipboard"
          >
            📋
          </button>
        </div>
        <div ref={scrollBoxRef} className={styles.scrollBox}>
          {moveHistory.map((move, index) => {
            try {
              return (
                <div
                  key={index}
                  className={`${styles.fenEntry} ${selectedMove === index ? styles.selected : ''} ${move.isPreviousGameResult ? styles.previousGame : ''} ${move.isGameEndNotification ? styles.gameEndNotification : ''}`}
                  onClick={(e) => {
                    // Allow text selection: only trigger click if no text is selected
                    const selection = window.getSelection();
                    if (!selection || selection.toString().length === 0) {
                      handleMoveClick(index);
                    }
                  }}
                >
                  {gameState.mode !== Mode.Sandbox && (
                    <span className={styles.moveNumber}>
                      {move.isGameEndNotification ? "!" : move.turnNumber}
                    </span>
                  )}
                  <div className={styles.moveContent}>
                    {move.isGameEndNotification ? (
                      <>
                        <div className={styles.victoryHeadline}>{move.victoryHeadline}</div>
                        <div className={styles.victoryDetails}>{move.victoryDetails}</div>
                      </>
                    ) : (
                      <>
                        <div className={styles.simplified}>{move.moveDescription}</div>
                        {!move.isPreviousGameResult && (
                          <pre className={styles.fenNotation} title={getDisplayFEN(move.fen)}>
                            {getDisplayFEN(move.fen)}
                          </pre>
                        )}
                      </>
                    )}
                  </div>
                  
                </div>
              );
            } catch (err) {
              return <div key={index} style={{ color: 'red' }}>Error rendering FEN entry</div>;
            }
          })}
        </div>
        
        {/* Popup menu positioned outside the container */}
        {showPopup !== null && !moveHistory[showPopup]?.isGameEndNotification && (
          <div 
            ref={popupRef}
            className={`${styles.popup} ${isDarkMode ? styles.popupDark : ''}`}
          >
            <div className={`${styles.popupInner} ${isDarkMode ? styles.popupInnerDark : ''}`}>
              <button
                className={`${styles.popupButton} ${isDarkMode ? styles.popupButtonDark : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyFEN(showPopup);
                }}
              >
                Copy FEN
              </button>
              <button
                className={`${styles.popupButton} ${isDarkMode ? styles.popupButtonDark : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRevertToFEN(showPopup);
                }}
              >
                Revert to FEN
              </button>
            </div>
          </div>
        )}

        {/* Copy confirmation popup */}
        {showCopyConfirmation && (
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: isDarkMode ? '#1a1a2e' : '#f5f3ef',
              color: isDarkMode ? 'rgba(240, 245, 255, 0.9)' : '#2a2520',
              padding: '16px 32px',
              borderRadius: '2px',
              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: isDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 4px 16px rgba(0, 0, 0, 0.1)',
              zIndex: 10000,
              fontSize: '13px',
              fontWeight: 300,
              letterSpacing: '1px',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
              pointerEvents: 'none'
            }}
          >
            Move history copied to clipboard
          </div>
        )}
      </div>
    );
  } catch (err) {
    return <div style={{ color: 'red' }}>[FENHistory] Error rendering FEN history: {String(err)}</div>;
  }
});

export default FENHistory;
